import { useSessionApp } from '../../../../utils/actionableNotifications';
import React, { useState, useEffect, useRef } from "react";
import { Alert, Card, Tag, Tooltip, Button, Image, Empty, Input, InputNumber, Divider } from "antd";
import { Select } from "../../../shared/OptionWidthControl";
import { PlusOutlined, EditOutlined, StopOutlined, DollarOutlined } from "@ant-design/icons";
import { useQv4Theme, sideLabel, conditionLabel, loadSideLabels, loadConditionLabels } from "../../helpers";
import { API_DOMAIN } from "../../../constants";
import { patchQueryPart } from "../../models/shared/queryPartsModel";
import { uploadImage, downloadChatAttachment } from "../../models/shared/mediaModel";
import { fetchCustomerChatImages } from "../../models/workflow-drawer/chatImagesModel";
import { fetchMiscMode } from "../../models/shared/miscModeModel";
import { isOptionActive } from "../../models/shared/dropdownValuesModel";
import { useTaskParts } from "../../viewmodels/workflow-drawer/useTaskParts";
import { addPartInfo } from "../../models/shared/partInfoModel";
import { PartsEditor, EMPTY_PART } from "../drafts/PartsEditor";
import { QueryImagePicker } from "../drafts/QueryImagePicker";
import { hasPartSelection } from "../../models/shared/queryPartPayload";
import { normalizeTaskImages } from "../../models/shared/taskDetailModel";
import { availablePartQuotes, selectedPartQuote } from "../../models/price-drawer/quoteEditingPolicy";
import { partEditFields, validatedPartEdit } from "../../models/workflow-drawer/partEditingPolicy";
import { usePhotoTags } from '../../../../features/query-photos/viewmodels/usePhotoTags';
import { TaggedPhotoTile } from '../../../../features/query-photos/views/TaggedPhotoTile';
import { PartPhotoRow } from '../../../../features/query-photos/views/PartPhotoRow';
import { imageIdentity } from '../../../../features/query-photos/models/photoTags';

const fmtAED = (val) => val != null ? `AED ${Number(val).toLocaleString()}` : "—";

export function PartsTab({
  parts, images, queryPk, userId, customerPhone, onPartPatched, onManagePrices,
  canManageParts = false, canEditQuery = false,
}) {
  const { message } = useSessionApp();
  const { t, isDark } = useQv4Theme();
  const { disable, enable, add, appendImages } = useTaskParts(userId);
  const safeImages = normalizeTaskImages(images);
  const photoTags = usePhotoTags(queryPk);

  const [, setLabelsReady] = useState(false);
  useEffect(() => { loadConditionLabels().then(() => setLabelsReady(v => !v)); }, []);
  useEffect(() => { loadSideLabels().then(() => setLabelsReady(v => !v)); }, []);

  const [sideOptions, setSideOptions] = useState([]);
  useEffect(() => { fetchMiscMode("part_side").then(list => setSideOptions((list || []).map(o => ({ value: o.value, label: o.label, disabled: !isOptionActive(o) })))); }, []);
  const [conditionOptions, setConditionOptions] = useState([]);
  useEffect(() => { fetchMiscMode("part_condition_origin").then(list => setConditionOptions((list || []).map(o => ({ value: o.value, label: o.label, disabled: !isOptionActive(o) })))); }, []);

  const [cardMode, setCardMode] = useState({});
  const [editFields, setEditFields] = useState({});
  const [disableReason, setDisableReason] = useState({});
  const [saving, setSaving] = useState({});
  const [addMode, setAddMode] = useState(false);
  const [newParts, setNewParts] = useState([{ ...EMPTY_PART }]);
  const [chatImages, setChatImages] = useState([]);
  const [chatImagesLoaded, setChatImagesLoaded] = useState(false);
  const [chatImagesLoading, setChatImagesLoading] = useState(false);
  // Standalone photo-add flow — separate from Add Part below on purpose.
  // handleAddParts requires at least one valid part before it will submit
  // anything, so a picture with no accompanying part had no way to reach the
  // query; this flow calls appendImages directly, which was already an
  // independent operation under the hood (handleAddParts just always called
  // it in the same breath as adding parts).
  const [addPhotosMode, setAddPhotosMode] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoSavePending = useRef(false);
  // Shared between the two add flows above/below — either one opening
  // should be able to trigger this fetch, and it only needs to happen once
  // regardless of which one asked for it first.
  useEffect(() => {
    if (!(addMode || addPhotosMode) || chatImagesLoaded || !customerPhone) return;
    setChatImagesLoaded(true);
    setChatImagesLoading(true);
    fetchCustomerChatImages(customerPhone, userId).then(setChatImages).finally(() => setChatImagesLoading(false));
  }, [addMode, addPhotosMode, chatImagesLoaded, customerPhone, userId]);
  const [adding, setAdding] = useState(false);

  const resetAddPhotosForm = () => {
    setAddPhotosMode(false);
    setNewPhotos([]);
  };

  const handleAddPhotos = async () => {
    if (photoSavePending.current) return;
    if (newPhotos.length === 0) { message.warning("Add at least one photo"); return; }
    photoSavePending.current = true;
    setUploadingPhotos(true);
    try {
      setNewPhotos(imgs => imgs.map(img => ({ ...img, uploadStatus: "uploading" })));
      const results = await Promise.all(newPhotos.map(async img => {
        try {
          let file;
          if (img.source === "upload") {
            file = img.file;
          } else {
            const { blob } = await downloadChatAttachment(img.attachmentId);
            const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
            file = new File([blob], `chat_image_${Date.now()}.${ext}`, { type: blob.type });
          }
          const data = await uploadImage(file);
          setNewPhotos(imgs => imgs.map(x => x.id === img.id ? { ...x, uploadStatus: "done" } : x));
          return { id: img.id, data, ok: true };
        } catch {
          setNewPhotos(imgs => imgs.map(x => x.id === img.id ? { ...x, uploadStatus: "error" } : x));
          return { id: img.id, ok: false };
        }
      }));
      if (results.some(r => !r.ok)) {
        message.error("Failed to upload one or more images. Please try again.");
        return;
      }
      await appendImages(queryPk, results.map(r => r.data));
      onPartPatched?.();
      resetAddPhotosForm();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to add photo(s)");
    } finally {
      photoSavePending.current = false;
      setUploadingPhotos(false);
    }
  };

  const startEdit = (p) => {
    setCardMode(m => ({ ...m, [p.id]: "editing" }));
    setEditFields(f => ({ ...f, [p.id]: {
      ...partEditFields(p, userId, sideOptions, conditionOptions),
    }}));
  };

  const endMode = (partId) => setCardMode(m => ({ ...m, [partId]: null }));

  const setField = (partId, key, val) =>
    setEditFields(f => ({ ...f, [partId]: { ...f[partId], [key]: val } }));

  const handleSave = async (partId) => {
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await patchQueryPart(partId, validatedPartEdit(editFields[partId]));
      onPartPatched?.();
      endMode(partId);
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || "Failed to save changes");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const handleDisable = async (partId) => {
    const reason = (disableReason[partId] || "").trim();
    if (!reason) { message.warning("Please enter a reason"); return; }
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await disable(partId, reason);
      onPartPatched?.();
      endMode(partId);
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || "Failed to disable part");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const handleEnable = async (partId) => {
    setSaving(s => ({ ...s, [partId]: true }));
    try {
      await enable(partId);
      onPartPatched?.();
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || "Failed to re-enable part");
    } finally {
      setSaving(s => ({ ...s, [partId]: false }));
    }
  };

  const resetAddForm = () => {
    setAddMode(false);
    setNewParts([{ ...EMPTY_PART }]);
  };

  const handleAddNewPartInfoLocal = async (value, idx) => {
    try {
      await addPartInfo(value);
      setNewParts(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], uzdpart360_part_id: null, part_name: value };
        return next;
      });
      return true;
    } catch {
      message.error("Failed to save new part name");
      return false;
    }
  };

  const handleAddParts = async () => {
    const validParts = newParts.filter(hasPartSelection);
    if (validParts.length === 0) { message.warning("Add at least one part"); return; }
    setAdding(true);
    try {
      await Promise.all(validParts.map(p => add(p, queryPk)));
      onPartPatched?.();
      resetAddForm();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to add part(s)");
    } finally {
      setAdding(false);
    }
  };

  const renderPartCard = (p) => {
    const selectedQuote = selectedPartQuote(p);
    const isSelected = (q) => selectedQuote === q;
    const quotes = availablePartQuotes(p);
    const hasQuotes = quotes.length > 0;
    const isDisabled = p.status === "disabled";
    const mode = cardMode[p.id] ?? null;
    const fields = editFields[p.id] ?? {};
    const isSaving = saving[p.id] ?? false;

    return (
      <Card key={p.id} size="small"
        style={{
          borderRadius: 8,
          opacity: isDisabled ? 0.75 : 1,
          border: isDisabled ? "1px solid #ffa39e" : undefined,
          background: isDisabled ? (isDark ? "rgba(255,77,79,0.12)" : "#fff2f0") : undefined,
        }}
        styles={{ body: { padding: "10px 14px" } }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{
            fontWeight: 700, fontSize: 13,
            textDecoration: isDisabled ? "line-through" : "none",
            color: isDisabled ? "#cf1322" : undefined,
          }}>
            {p.part_name || `Part #${p.id}`}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {isDisabled
              ? <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Disabled</Tag>
              : <Tag style={{ margin: 0, fontSize: 10 }}>{p.status_label || p.status}</Tag>
            }
            {!isDisabled && canManageParts && (
              <Button size="small" type="text"
                disabled={isSaving}
                icon={<EditOutlined style={{ fontSize: 11 }} />}
                style={{ color: mode === "editing" ? "var(--app-color-accent)" : t.textSub }}
                onClick={() => mode === "editing" ? endMode(p.id) : startEdit(p)}
              />
            )}
            {!isDisabled && canManageParts && !hasQuotes && mode !== "disabling" && (
              <Tooltip title="Disable this part" getPopupContainer={n => n.parentElement ?? document.body}>
                <Button size="small" type="text" danger
                  icon={<StopOutlined style={{ fontSize: 11 }} />}
                  onClick={() => setCardMode(m => ({ ...m, [p.id]: "disabling" }))}
                />
              </Tooltip>
            )}
            {!isDisabled && canManageParts && hasQuotes && mode !== "disabling" && (
              <Tooltip title="Can't disable — suppliers have already quoted on this part" getPopupContainer={n => n.parentElement ?? document.body}>
                <Button size="small" type="text" disabled icon={<StopOutlined style={{ fontSize: 11 }} />} />
              </Tooltip>
            )}
            {isDisabled && canManageParts && (
              <Button size="small" loading={isSaving}
                style={{ fontSize: 10, height: 22, padding: "0 8px" }}
                onClick={() => handleEnable(p.id)}
              >
                Re-enable
              </Button>
            )}
          </div>
        </div>

        {!isDisabled && <PartPhotoRow data={photoTags.data} partId={p.id} />}
        {isDisabled && (p.remarks || p.remark) && (
          <div style={{ fontSize: 11, color: "#cf1322", marginBottom: 4 }}>
            Reason: {p.remarks || p.remark}
          </div>
        )}

        {mode === "editing" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Side</div>
                <Select size="small" style={{ width: "100%" }} allowClear
                  value={fields.side === "" ? null : fields.side ?? null}
                  disabled={isSaving}
                  onChange={v => setField(p.id, "side", v ?? "")}
                  options={sideOptions}
                  placeholder="Side"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Condition</div>
                <Select size="small" style={{ width: "100%" }} allowClear
                  value={fields.condition_preference === "" ? null : fields.condition_preference ?? null}
                  disabled={isSaving}
                  onChange={v => setField(p.id, "condition_preference", v ?? "")}
                  options={conditionOptions}
                  placeholder="Condition"
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Part Number</div>
                <Input size="small" value={fields.part_number} disabled={isSaving}
                  onChange={e => setField(p.id, "part_number", e.target.value)}
                  placeholder="OEM or aftermarket #"
                />
              </div>
              <div style={{ width: 84 }}>
                <div style={{ fontSize: 11, color: t.textSub, marginBottom: 2 }}>Qty</div>
                <InputNumber size="small" min={1} step={1} value={fields.quantity} disabled={isSaving}
                  onChange={val => setField(p.id, "quantity", val)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <Button size="small" disabled={isSaving} onClick={() => endMode(p.id)}>Cancel</Button>
              <Button size="small" type="primary" loading={isSaving} onClick={() => handleSave(p.id)}>Save</Button>
            </div>
          </div>
        )}

        {mode === "disabling" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: t.textSub, marginBottom: 4 }}>Reason for disabling</div>
            <Input.TextArea rows={2} size="small" autoFocus
              placeholder="Why is this part being removed?"
              value={disableReason[p.id] || ""}
              onChange={e => setDisableReason(r => ({ ...r, [p.id]: e.target.value }))}
              style={{ marginBottom: 6 }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <Button size="small" onClick={() => endMode(p.id)}>Cancel</Button>
              <Button size="small" danger type="primary" loading={isSaving} onClick={() => handleDisable(p.id)}>
                Confirm Disable
              </Button>
            </div>
          </div>
        )}

        {mode === null && (
          <div style={{ fontSize: 11, color: t.textSub, marginBottom: hasQuotes && !isDisabled ? 8 : 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <span>
              Qty: {p.quantity}
              {p.side != null && p.side !== "" ? ` · ${p.side_label || sideLabel(p.side)}` : ""}
              {p.condition_preference != null && p.condition_preference !== "" ? ` · ${p.condition_preference_label || conditionLabel(p.condition_preference)}` : ""}
              {p.part_number ? ` · Part# ${p.part_number}` : ""}
              {selectedQuote && p.selling_price != null ? ` · Selling: ${fmtAED(p.selling_price)}` : ""}
            </span>
            {/* Not yet persisted on query_parts — see FUTURE_TASKS.md's
                query_parts.old_exchange spec — so this stays inert until
                that backend column ships. */}
            {(p.old_exchange === true || p.old_exchange === 1 || p.old_exchange === "1") && <Tag color="gold" style={{ margin: 0, fontSize: 10 }}>With Old Exchange</Tag>}
          </div>
        )}

        {!isDisabled && hasQuotes && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.textSub, marginBottom: 2 }}>
              Supplier Quotes ({quotes.length})
            </div>
            {quotes.map(q => (
              <div key={q.id} style={{
                padding: "6px 10px", borderRadius: 6, fontSize: 11,
                border: isSelected(q) ? "1.5px solid #52c41a" : `1px solid ${t.border}`,
                background: isSelected(q) ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : t.headerBg,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {q.supplier_name || `Supplier #${q.supplier_id}`}
                  </span>
                  <span style={{ color: t.textSub }}>
                    {q.quoted_price != null ? fmtAED(q.quoted_price) : "—"}
                    {q.delivery_cost != null ? ` · Delivery: ${fmtAED(q.delivery_cost)}` : ""}
                    {q.warranty_days ? ` · ${q.warranty_days}d warranty` : ""}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  {isSelected(q) && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>Selected</Tag>}
                  {q.availability_status && <Tag style={{ margin: 0, fontSize: 10 }}>{q.availability_status}</Tag>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  const activeParts = parts.filter(p => p.status !== "disabled");
  const disabledParts = parts.filter(p => p.status === "disabled");

  return (
    <Image.PreviewGroup>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {onManagePrices && (
        <Button size="small" icon={<DollarOutlined />} onClick={onManagePrices} style={{ alignSelf: "flex-start" }}>
          Manage Prices
        </Button>
      )}
      {(safeImages.length > 0 || canEditQuery) && (
          <div className="parts-tab-photo-strip" aria-label="Query photos">
            {canEditQuery && !addMode && !addPhotosMode && (
              <Button type="dashed" onClick={() => setAddPhotosMode(true)}
                style={{ width: 72, height: 72, flexShrink: 0, borderRadius: 6, color: t.textSub,
                  borderColor: t.textSub, borderWidth: 2, padding: 4, display: "flex", flexDirection: "column", gap: 6 }}>
                <PlusOutlined />
                <span style={{ fontSize: 11 }}>Add Photos</span>
              </Button>
            )}
            {safeImages.map(src => {
              const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
              return (
                <TaggedPhotoTile key={imageIdentity(src)} src={url} photo={photoTags.photo(src)} parts={parts}
                  editable={canManageParts && Boolean(photoTags.data)} disabled={photoTags.busy}
                  onChange={ids => photoTags.save({ [src]: ids })} />
              );
            })}
          </div>
      )}
      {photoTags.notice && <Alert type="warning" showIcon message={photoTags.notice} style={{ fontSize: 12, padding: '6px 10px' }} />}
      {addPhotosMode && (
          <QueryImagePicker
            modalOnly
            open={addPhotosMode}
            onCancel={resetAddPhotosForm}
            onConfirm={handleAddPhotos}
            saving={uploadingPhotos}
            images={newPhotos}
            setImages={updater => setNewPhotos(prev => typeof updater === "function" ? updater(prev) : updater)}
            imageMessages={chatImages}
            imageMessagesLoading={chatImagesLoading}
            label="New photos"
          />
      )}

      {!addMode && !addPhotosMode && canManageParts && (
        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setAddMode(true)}
          style={{ minHeight: 84, borderRadius: 8, color: t.textSub, borderColor: t.textSub, borderWidth: 2 }}>
          Add Part
        </Button>
      )}
      {addMode && (
        <Card size="small" style={{ borderRadius: 8, border: `1.5px dashed ${t.border}` }}
          styles={{ body: { padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 } }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: t.text }}>Add to this query</div>
          <PartsEditor
            parts={newParts}
            setParts={updater => setNewParts(prev => typeof updater === "function" ? updater(prev) : updater)}
            sideOptions={sideOptions}
            conditionOptions={conditionOptions}
            onAddNewPartInfo={handleAddNewPartInfoLocal}
            queryPk={queryPk}
            label="New parts"
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
            <Button size="small" disabled={adding} onClick={resetAddForm}>Cancel</Button>
            <Button size="small" type="primary" loading={adding} onClick={handleAddParts}>Save</Button>
          </div>
        </Card>
      )}
      {activeParts.length === 0 && !addMode && (
        <Empty description="No parts added" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "24px 0 8px" }} />
      )}
      {activeParts.map(renderPartCard)}
      {disabledParts.length > 0 && (
        <>
          <Divider style={{ margin: "4px 0", fontSize: 11, color: t.textSub }}>
            Disabled ({disabledParts.length})
          </Divider>
          {disabledParts.map(renderPartCard)}
        </>
      )}
    </div>
    </Image.PreviewGroup>
  );
}
